/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { reaction, computed } from 'mobx';
import { translate } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { TranslatorProps } from 'react-i18next';

import { isIE } from 'utils/browserDetection';
import navigation from 'services/NavigationService';
import DashboardStore from 'stores/DashboardStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import NivoBarChart from '../../components/NivoBarChart';
import type { CommonWidgetProps } from '../../commonTypes';
import NavigationService from 'services/NavigationService';
import { formatAssetTypeParam } from 'routes/asset';

const STATIC_DATA = [
  {
    id: 'cd0d1ad5-77a5-4a58-ad63-11ae2aff01a5',
    name: 'Model 32513',
    description:
      'Et voluptatem et autem. Reiciendis rerum provident omnis deleniti sapiente exercitationem. Ipsa ut occaecati asperiores esse. Repudiandae et quia at officiis doloribus asperiores.',
    manufacturer_name: 'Analog Devices',
    asset_count: {
      all: 5,
      owned: 2,
      rented: 3,
    },
  },
  {
    id: '2bf14a44-f3db-4528-b3b3-43c2b820ec24',
    name: 'Model 11563',
    description: 'Iure assumenda laborum rem excepturi et veniam. Sit porro hic laudantium.',
    manufacturer_name: 'C.Itoh',
    asset_count: {
      all: 12,
      owned: 4,
      rented: 8,
    },
  },
  {
    id: '023e7c38-86ec-41f7-9fbe-2067dab486c6',
    name: 'Model 94131',
    description:
      'Qui sed rerum blanditiis beatae. Cum officiis quis dignissimos aut necessitatibus nesciunt eveniet. Qui aut ea dolorem est voluptatem. Unde explicabo aut dolor ut nobis id veritatis eos.',
    manufacturer_name: 'Anritsu',
    asset_count: {
      all: 10,
      owned: 8,
      rented: 2,
    },
  },
];

export type AssetsByModelProps = CommonWidgetProps;

type InjectedProps = {
  dashboardStore: DashboardStore,
} & TranslatorProps;

function firstToUpperCase(str) {
  return str.toUpperCase();
}

@translate()
@inject('dashboardStore')
@observer
export default class AssetsByModel extends ComponentWithInjectedProps<
  AssetsByModelProps,
  {},
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || AssetsByModel.LOAD_THUMBNAIL;
  }

  @computed get widgetData(): any[] | null {
    const { dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return STATIC_DATA;
    }

    return dashboardStore.assetsByModel ? dashboardStore.assetsByModel['assets-products'] : null;
  }

  autoUpdateDisposer: Function;

  componentDidMount() {
    const { dashboardStore } = this.props;

    this.loadWidgetData(dashboardStore.loadedTradingAccount);

    this.autoUpdateDisposer = reaction(
      () => dashboardStore.loadedTradingAccount,
      ta => {
        this.loadWidgetData(ta);
      }
    );
  }

  loadWidgetData = (tradingAccount: string) => {
    const { dashboardStore } = this.props;

    if (this.shouldLoadData) {
      dashboardStore.loadAssetsByModel(dashboardStore.loadedTradingAccount);
    }
  };

  componentWillUnmount() {
    this.autoUpdateDisposer();
  }

  render() {
    const { dashboardStore, t, size, widgetItem, isFullScreen } = this.props;
    const accountId = dashboardStore.loadedTradingAccount;

    const modelData = this.widgetData
      ? this.widgetData
          .reverse()
          .map((asset, index) => {
            return {
              model: asset.name,
              product_id: asset.id,
              manufacturer: asset.manufacturer_name,
              description: asset.description,
              owned: asset.asset_count.owned,
              owned_color: 'hsl(203, 54%, 46%)',
              rented: asset.asset_count.rented,
              rented_color: 'hsl(200, 92%, 55%)',
              sale: asset.asset_count.sale,
              sale_color: 'hsl(200, 100%, 79%)',
            };
          })
          .filter(obj => obj !== null)
      : null;

    return (
      <NivoBarChart
        widgetInfos={[{ title: t('general:dashboards.assets_by_model'), variant: 'default' }]}
        tableTitles={[t('general:model'), t('general:dashboards.abbrs.number_of_assets')]}
        widgetItem={widgetItem}
        isFullScreen={isFullScreen}
        size={this.props.size}
        isIE={isIE}
        data={modelData}
        isLoading={this.shouldLoadData && dashboardStore.loadingAssetsByModel}
        legendKeys={[
          {
            label: t('general:assets.owned_asset_plural'),
            color: 'hsl(203, 54%, 46%)',
          },
          {
            label: t('general:assets.rented_asset_plural'),
            color: 'hsl(200, 92%, 55%)',
          },
          {
            label: t('general:assets.sale_asset_plural'),
            color: 'hsl(200, 100%, 79%)',
          },
        ]}
        button={true}
        buttonLink={NavigationService.formulateReportViewUri('/assets', {
          page: 1,
          filters: [
            ...(accountId ? [{ key: 'account', values: [accountId] }] : []),
            { key: 'asset_type', values: ['Rented Open', 'Owned', 'Purchased'] },
          ],
        })}
        buttonText={t('general:actions.view_asset_plural')}
        // Nivo bar chart props
        indexBy="product_id"
        keys={['owned', 'rented', 'sale']}
        colorBy={function(e: any): string {
          return e.data[e.id + '_color'];
        }}
        axisLeft={{
          renderTick: tick => {
            const modelName: string = modelData[tick.tickIndex]
              ? modelData[tick.tickIndex].model || ''
              : '';

            const abbrvName: string =
              modelName && modelName.length > 16 ? modelName.substring(0, 16) + '...' : modelName;

            return (
              <g key={tick.key} transform={`translate(${tick.x},${tick.y})`}>
                <text textAnchor="end" alignmentBaseline="central" transform="translate(-12, 0)">
                  <Link
                    to={NavigationService.formulateReportViewUri('/assets', {
                      page: 1,
                      filters: [
                        ...(accountId ? [{ key: 'account', values: [accountId] }] : []),
                        { key: 'model', values: [modelName] },
                        { key: 'asset_type', values: ['Rented Open', 'Owned', 'Purchased'] },
                      ],
                    })}
                  >
                    {abbrvName}
                  </Link>
                </text>
              </g>
            );
          },
        }}
        onClick={e => {
          const uri = NavigationService.formulateReportViewUri('/assets', {
            page: 1,
            filters: [
              ...(accountId ? [{ key: 'account', values: [accountId] }] : []),
              { key: 'model', values: [e.data.model] },
              {
                key: 'asset_type',
                values: [
                  ...(e.id === 'sale' ? ['Purchased'] : formatAssetTypeParam(e.id.toLowerCase())),
                ],
              },
            ],
          });
          navigation.navigateTo(uri);
        }}
        tooltip={function(data) {
          return (
            <div>
              <b>
                {data.data.manufacturer} {data.data.model}
              </b>
              <br />
              No. of {data.id.replace(/\b[a-z]/, firstToUpperCase)} assets: <b>{data.value}</b>
              <div style={{ maxWidth: '300px' }}>{data.data.description}</div>
            </div>
          );
        }}
      />
    );
  }
}
